/**
 * Professional useTranslation Hook
 * Provides translation functionality with comprehensive error handling and performance optimization
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translationService from '../services/translationService';

/**
 * Custom hook for translation functionality
 * @returns {Object} Translation functions and utilities
 */
export const useTranslation = () => {
  const { locale, setLocale } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize translation service when component mounts
  useEffect(() => {
    const initializeTranslations = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        await translationService.initialize({
          defaultLanguage: locale,
          fallbackLanguage: 'fr'
        });
        
        await translationService.changeLanguage(locale);
      } catch (err) {
        console.error('Translation initialization error:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTranslations();
  }, [locale]);

  // Update translation service when language changes
  useEffect(() => {
    const updateLanguage = async () => {
      try {
        setError(null);
        await translationService.changeLanguage(locale);
      } catch (err) {
        console.error('Language change error:', err);
        setError(err.message);
      }
    };

    if (translationService.getCurrentLanguage() !== locale) {
      updateLanguage();
    }
  }, [locale]);

  /**
   * Translation function with comprehensive options
   * @param {string} key - Translation key
   * @param {string|Object} defaultValueOrOptions - Default value or options object
   * @param {Object} optionsParam - Options when second param is string
   */
  const t = useCallback((key, defaultValueOrOptions, optionsParam) => {
    // Handle different parameter patterns
    let options = {};
    let defaultValue = key;

    if (typeof defaultValueOrOptions === 'string') {
      defaultValue = defaultValueOrOptions;
      options = optionsParam || {};
    } else if (typeof defaultValueOrOptions === 'object') {
      options = defaultValueOrOptions || {};
      defaultValue = options.defaultValue || key;
    }

    // Validate key
    if (!key || typeof key !== 'string') {
      console.warn('Translation key must be a non-empty string');
      return defaultValue;
    }

    try {
      return translationService.translate(key, {
        defaultValue,
        language: locale,
        ...options
      });
    } catch (err) {
      console.error(`Translation error for key "${key}":`, err);
      return defaultValue;
    }
  }, [locale]);

  /**
   * Get translation with return objects support
   * @param {string} key - Translation key
   * @param {Object} options - Translation options
   */
  const tArray = useCallback((key, options = {}) => {
    return t(key, { ...options, returnObjects: true });
  }, [t]);

  /**
   * Check if a translation key exists
   * @param {string} key - Translation key to check
   */
  const exists = useCallback((key) => {
    if (!key || typeof key !== 'string') return false;
    
    try {
      const result = translationService.translate(key, { defaultValue: null });
      return result !== null && result !== key;
    } catch {
      return false;
    }
  }, []);

  /**
   * Change language programmatically
   * @param {string} language - Language code
   */
  const changeLanguage = useCallback(async (language) => {
    try {
      setError(null);
      const success = await translationService.changeLanguage(language);
      
      if (success) {
        setLocale(language);
        return true;
      }
      
      throw new Error(`Failed to change language to ${language}`);
    } catch (err) {
      console.error('Language change error:', err);
      setError(err.message);
      return false;
    }
  }, [setLocale]);

  // Memoized i18n object for performance
  const i18n = useMemo(() => ({
    language: locale,
    languages: translationService.getAvailableLanguages(),
    changeLanguage,
    isLoading,
    error,
    exists,
    getCacheStats: () => translationService.getCacheStats(),
    clearCache: () => translationService.clearCache()
  }), [locale, changeLanguage, isLoading, error, exists]);

  return {
    t,
    tArray,
    i18n,
    isLoading,
    error,
    ready: !isLoading && !error
  };
};

/**
 * HOC for components that need translation
 * @param {React.Component} Component - Component to wrap
 */
export const withTranslation = (Component) => {
  const WrappedComponent = (props) => {
    const translation = useTranslation();
    return <Component {...props} {...translation} />;
  };
  
  WrappedComponent.displayName = `withTranslation(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default useTranslation;
